import { useState, type ChangeEvent } from 'react'
import { Link } from 'react-router-dom'

const API_BASE = import.meta.env.PROD
  ? 'https://derm-api.brian-mabry-edwards.workers.dev'
  : ''

interface SearchResult {
  image_id: string
  patient_id: string
  storage_path: string
  similarity: number
  patient_name: string
  image_url: string | null
}

interface SearchResponse {
  results: SearchResult[]
  query_embedding_dimensions: number
  match_count: number
  threshold: number
}

export default function Search() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SearchResult[]>([])
  const [searchMeta, setSearchMeta] = useState<{ threshold: number; count: number } | null>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setError(null)
      setResults([])
      setSearchMeta(null)
    }
  }

  const handleSearch = async () => {
    if (!selectedFile) return
    setIsSearching(true)
    setError(null)
    setResults([])

    try {
      const formData = new FormData()
      formData.append('image', selectedFile)
      formData.append('match_count', '20')
      formData.append('threshold', '0.5')

      const response = await fetch(`${API_BASE}/api/v1/search/similar`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Search failed: ${response.status}`)
      }

      const data: SearchResponse = await response.json()
      setResults(data.results)
      setSearchMeta({ threshold: data.threshold, count: data.match_count })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed'
      setError(message)
      console.error('Search error:', err)
    } finally {
      setIsSearching(false)
    }
  }

  const formatSimilarity = (similarity: number): string => {
    return `${(similarity * 100).toFixed(1)}%`
  }

  const getSimilarityColor = (similarity: number): string => {
    if (similarity >= 0.9) return 'text-green-600 bg-green-100'
    if (similarity >= 0.7) return 'text-yellow-600 bg-yellow-100'
    return 'text-gray-600 bg-gray-100'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Find Similar Cases</h1>
        <p className="mt-1 text-gray-600">
          Upload an image to find visually similar cases in the database
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
          <h3 className="text-red-800 font-bold text-lg">ERROR</h3>
          <pre className="text-red-700 text-sm mt-2 whitespace-pre-wrap font-mono">{error}</pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Reference Image Upload */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Reference Image</h2>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer"
            onClick={() => document.getElementById('file-input')?.click()}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-64 mx-auto rounded"
              />
            ) : (
              <div className="text-gray-500">
                <p className="text-lg">Click to upload an image</p>
                <p className="text-sm mt-1">PNG, JPG up to 10MB</p>
              </div>
            )}
            <input
              id="file-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          {selectedFile && (
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Selected: {selectedFile.name}
              </p>
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="mt-3 w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? 'Searching...' : 'Find Similar Cases'}
              </button>
            </div>
          )}
        </div>

        {/* Search Results */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Search Results</h2>
            {searchMeta && (
              <span className="text-sm text-gray-500">
                {results.length} matches found
              </span>
            )}
          </div>

          {!selectedFile && !isSearching && results.length === 0 && (
            <div className="text-center text-gray-500 py-12">
              <p>Upload an image to see similar cases</p>
              <p className="text-sm mt-1">Results will appear here</p>
            </div>
          )}

          {isSearching && (
            <div className="text-center text-gray-500 py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent mb-4"></div>
              <p>Analyzing image...</p>
              <p className="text-sm mt-1">Generating embedding and searching database</p>
            </div>
          )}

          {!isSearching && selectedFile && results.length === 0 && !error && searchMeta && (
            <div className="text-center text-gray-500 py-12">
              <p>No similar cases found</p>
              <p className="text-sm mt-1">Try uploading a different image or adjusting the threshold</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="grid grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
              {results.map((result) => (
                <Link
                  key={result.image_id}
                  to={`/patients/${result.patient_id}`}
                  className="block group"
                >
                  <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    {result.image_url ? (
                      <img
                        src={result.image_url}
                        alt={`Match for ${result.patient_name}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No preview
                      </div>
                    )}
                    {/* Similarity badge */}
                    <div
                      className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${getSimilarityColor(result.similarity)}`}
                    >
                      {formatSimilarity(result.similarity)}
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 truncate">
                      {result.patient_name}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search info */}
      {searchMeta && (
        <div className="text-center text-xs text-gray-400">
          Searched with threshold: {(searchMeta.threshold * 100).toFixed(0)}% | Max results: {searchMeta.count}
        </div>
      )}
    </div>
  )
}
